import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGetLoginUserprivilege } from '../../../../api/auth/auth';
import { GetPsUpdateListParams, PsTemp } from '../../../../api/model';
import { usePostPsCheckDelete } from '../../../../api/ps-checks/ps-checks';
import { useGetPsChapterList, useGetPsTempList } from '../../../../api/pss/pss';
import { useDefaultOptions } from '../../../../hooks/api';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import ErrorComponent from '../../../atoms/ErrorComponent';
import NoDataComponent from '../../../atoms/NoDataComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import PageNavi from '../../../modules/PageNavi';
import SpinnerModal from '../../../modules/SpinnerModal';
import {
  DialogType,
  Message,
  TempState,
  getCheckMarkType,
  getDialogMessage,
  getDialogTypeFromLoginInfo,
  getDialogTypeStr,
  getFormatDateYYYYMMDD,
  getTempState,
} from './PsListCommmon';
import { SortableTh } from './PsListForm';

export interface PsTemporaryListTabHandler {
  refresh: () => Promise<void>;
}

interface Props {
  callModal: (psVersionId: number | undefined, tempState: number, selectedIndex: number) => void;
}

/**
 * 改廃案一覧タブ
 */
const PsTemporaryListTab = forwardRef<PsTemporaryListTabHandler, Props>(({ callModal }, ref) => {
  // 1ページに表示する行数:50行
  const N_DATA_PER_PAGE = 50;

  // 翻訳用定数
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  // 選択中章情報保持
  const defaultCharterId = sessionStorageUtils.get('Pss.Temporary.TempChapterId', '');
  const [selectedCharterId, setSelectedCharterId] = useState(defaultCharterId);

  // 選択中作成者情報保持
  const defaultAuthor = sessionStorageUtils.get('Pss.Temporary.TempAuthor', '');
  const [selectedAuthor, setSelectedAuthor] = useState(defaultAuthor);

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  /* PS章一覧取得 */
  const {
    data: psChapterListData,
    isLoading: psChapterListIsLoading,
    isError: psChapterListIsError,
    error: psChapterListError,
  } = useGetPsChapterList(options);
  const chapterPsIdNum =
    selectedCharterId === 'all' || selectedCharterId === '' ? undefined : Number(selectedCharterId);
  const createUserIdNum = selectedAuthor === 'all' || selectedAuthor === '' ? undefined : Number(selectedAuthor);
  const updateListParams: GetPsUpdateListParams = { chapter_ps_id: chapterPsIdNum, create_user_id: createUserIdNum };

  // ログインユーザ情報取得
  const {
    data: loginUserData,
    isLoading: loginUserIsLoading,
    isError: loginUserIsError,
    error: loginUserError,
  } = useGetLoginUserprivilege(options);
  const uniqueUsers = useRef<PsTemp[]>();

  /* PS改廃案一覧取得 */
  const {
    data: psTempData,
    refetch: psTempRefetch,
    isLoading: psTempIsLoading,
    isError: psTempIsError,
    error: psTempError,
  } = useGetPsTempList(updateListParams, options);

  const [confirmDalogFlg, setConfirmDalogFlg] = useState<boolean>(false);
  const [messageDalogFlg, setMessageDalogFlg] = useState<boolean>(false);
  const [deleteErrorModal, setDeleteErrorModal] = useState(false);
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);
  const [deleteVersionId, setDeletePsNo] = useState(0);
  const dialogMessage = useRef<string>('');
  const [clicked, setClicked] = useState(false);

  /**
   * 削除マーク
   */
  function onClickDeleteMark(versionId: number | undefined) {
    setClicked(true);
    setDeletePsNo(versionId ?? 0);
  }

  /**
   * 削除ボタンクリック時
   */
  function OnDeleteBtnClick(state: number) {
    if (
      state === TempState.AWAITING_CONFIRMATION_PS ||
      state === TempState.AWAITING_CONFIRMATION_GL ||
      state === TempState.AWAITING_APPROVAL_MANAGER ||
      state === TempState.AWAITING_APPROVAL_MANAGER
    ) {
      dialogMessage.current = getDialogMessage(Message.CANNOT_DELETE_PS_TEMPORARY, t);
      // エラーダイアログの表示
      setMessageDalogFlg(true);
      return;
    }
    // 確認ダイアログの表示
    dialogMessage.current = getDialogMessage(Message.CONFIRM_DELETE_PS_TEMPORARY, t);
    setConfirmDalogFlg(true);
  }

  // 削除API呼出準備
  const { mutateAsync: deleteCheckAsync, error: deleteError } = usePostPsCheckDelete(options);

  // 削除成功時
  const onSuccessDelete = useCallback(() => {
    // 保存成功したらリフェッチする
    callModal(-1, -1, -1);
    void psTempRefetch();
    setConfirmDalogFlg(false);
    dialogMessage.current = getDialogMessage(Message.SUCCESS_DELETE_PS_TEMPORARY, t);
    setMessageDalogFlg(true);
  }, [callModal, psTempRefetch, t]);

  // 削除失敗時
  const onErrorDelete = useCallback(() => {
    setDisplaySpinnerModal(false);
    setConfirmDalogFlg(false);
    setDeleteErrorModal(true);
  }, []);

  // 削除メソッド
  const deleteTemp = useCallback(async () => {
    const psVersionId = deleteVersionId;
    // 削除実行
    await deleteCheckAsync(
      {
        psVersionId,
      },
      { onSuccess: onSuccessDelete, onError: onErrorDelete }
    );
  }, [deleteCheckAsync, deleteVersionId, onErrorDelete, onSuccessDelete]);

  /** ページネーション */
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Pss.Temporary.CurrentPage', 1));

  /** ソート */
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('Pss.Temporary.SortInfo', { key: ['updated_at'], isDisc: true })
  );

  /** ソート処理 */
  const allPss = useMemo(
    () =>
      [...(psTempData?.data.ps_temps ?? [])].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;

        // 配列にソートキーがセットされていない場合は何もしない
        if (sortInfo.key.length === 0) {
          return 0;
        }

        // 配列にソートキーが1つだけの場合
        if (sortInfo.key.length === 1) {
          return ((aAny[sortInfo.key[0]] ?? '') > (bAny[sortInfo.key[0]] ?? '') ? 1 : -1) * multiValue;
        }

        // 配列にソートキーが2つ以上の場合(2つとみなして並び替え)
        // （所属 + 作成者)はソートキーが2つある
        if ((aAny[sortInfo.key[0]] ?? '') !== (bAny[sortInfo.key[0]] ?? '')) {
          return ((aAny[sortInfo.key[0]] ?? '') > (bAny[sortInfo.key[0]] ?? '') ? 1 : -1) * multiValue;
        }
        return ((aAny[sortInfo.key[1]] ?? '') > (bAny[sortInfo.key[1]] ?? '') ? 1 : -1) * multiValue;
      }),
    [sortInfo, psTempData]
  );

  /** ページネーションで一覧をフィルタリングする */
  const filteredPss = useMemo(
    () => allPss.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, allPss]
  );

  /** ソートキーを保存 */
  const changeSortKey = useCallback((key: string[]) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: JSON.stringify(pre.key) === JSON.stringify(key) && !pre.isDisc };
      sessionStorageUtils.set('Pss.Temporary.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  /** ページネーションを保存 */
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Pss.Temporary.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  useEffect(() => {
    // ページネーションを1に戻す
    if (psTempData?.data.ps_temps != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }

    // 削除ボタン用イベント（削除メニュー表示後画面外クリックで削除メニュー画面非表示）
    const handleClick = () => {
      setClicked(false);
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [psTempData?.data.ps_temps]);

  // 親コンポーネントからの操作
  useImperativeHandle(ref, () => ({
    refresh: async () => {
      await psTempRefetch();
    },
  }));

  /* 章一覧のデータ */
  // ローディング
  if (psChapterListIsLoading) {
    return <WaitComponent />;
  }

  // エラー時
  if (psChapterListIsError) {
    return <ErrorComponent error={psChapterListError} />;
  }

  // ログインユーザ情報
  if (loginUserIsLoading) {
    return <WaitComponent />;
  }
  if (loginUserIsError) {
    return <ErrorComponent error={loginUserError} />;
  }
  /* PS改廃案一覧タブのデータ */
  // ローディング
  if (psTempIsLoading) {
    return <WaitComponent />;
  }

  // エラー時
  if (psTempIsError) {
    return <ErrorComponent error={psTempError} />;
  }

  // データがない場合
  if (
    psChapterListData == null ||
    psChapterListData.data == null ||
    psChapterListData.data.ps_chapters == null ||
    loginUserData == null ||
    loginUserData.data == null ||
    psTempData == null ||
    psTempData.data == null ||
    psTempData.data.ps_temps == null ||
    psTempData.data.ps_temps === undefined
  ) {
    return <NoDataComponent />;
  }

  /* PS作成者一覧取得 */
  if (
    (selectedCharterId === 'all' ||
      selectedCharterId === undefined ||
      selectedCharterId === null ||
      selectedCharterId === '') &&
    (selectedAuthor === 'all' || selectedAuthor === undefined || selectedAuthor === null || selectedAuthor === '')
  ) {
    uniqueUsers.current = Array.from(
      new Map(psTempData.data.ps_temps.map((user) => [user.create_user_id, user])).values()
    );
    sessionStorageUtils.set('Pss.Temporary.CreateUser', uniqueUsers.current);
  } else {
    uniqueUsers.current = sessionStorageUtils.get('Pss.Temporary.CreateUser', []);
  }

  return (
    <div>
      {/* 検索条件 */}
      <div className="ps-search">
        <div>
          <label htmlFor="chapter">{t('章の絞り込み')}</label>
          <span className="dropdown chapter">
            <select
              onChange={(e) => {
                setSelectedCharterId(e.target.value);
                sessionStorageUtils.set('Pss.Temporary.TempChapterId', e.target.value);
              }}
              value={selectedCharterId}
            >
              <option value="all" key="all">
                {t('すべて')}
              </option>
              {psChapterListData.data.ps_chapters.map((x) => (
                <option value={x.chapter_ps_id} key={x.chapter_ps_id}>
                  {x.chapter_ps_no}
                </option>
              ))}
            </select>
          </span>
        </div>
        <div>
          <label htmlFor="maker">{t('作成者の絞り込み')}</label>
          <span className="dropdown maker">
            <select
              onChange={(e) => {
                setSelectedAuthor(e.target.value);
                sessionStorageUtils.set('Pss.Temporary.TempAuthor', e.target.value);
              }}
              value={selectedAuthor}
            >
              <option value="all" key="all">
                {t('すべて')}
              </option>
              {uniqueUsers.current !== undefined
                ? uniqueUsers.current.map((x) => (
                    <option value={x.create_user_id} key={x.create_user_id}>
                      {x.create_user_membership_name} {x.create_user}
                    </option>
                  ))
                : ''}
            </select>
          </span>
        </div>
      </div>
      <table className="table28">
        <tbody>
          {/* ラベル クリックで並び替え */}
          <tr>
            <SortableTh label={t('管理No.')} onClick={() => changeSortKey(['ps_version_id'])} />
            <SortableTh label={t('タイトル')} onClick={() => changeSortKey(['title'])} />
            <SortableTh
              label={t('作成者')}
              onClick={() => changeSortKey(['create_user_membership_name', 'create_user'])}
            />
            <SortableTh label={t('状態')} onClick={() => changeSortKey(['temp_state'])} />
            <SortableTh label={t('最終更新日')} onClick={() => changeSortKey(['updated_at'])} />
            <SortableTh label={t('操作')} onClick={() => changeSortKey(['temp_state'])} />
            <th>{}</th>
            <th>{}</th>
          </tr>
          {/* データ表示部 */}
          {filteredPss.map((data, index) => (
            <tr key={data.ps_no?.toString()}>
              <td>
                <Link to={`/${language}/ps/view/${data.ps_version_id?.toString() ?? ''}`}> {data.ps_no}</Link>
              </td>
              <td>{data.title}</td>
              <td>
                {data.create_user_membership_name} {data.create_user}
              </td>
              <td>{getTempState(data.temp_state ?? -1, t)}</td>
              <td>{getFormatDateYYYYMMDD(data.updated_at)}</td>
              <td>
                <button
                  type="button"
                  className="text-button"
                  onClick={(e) => {
                    e.preventDefault();
                    callModal(data.ps_version_id, DialogType.STATE_HISTORY, index);
                  }}
                >
                  <span className="modal-open">
                    <i className="fa-solid fa-list" />
                    {getDialogTypeStr(DialogType.STATE_HISTORY, t)}
                  </span>
                </button>
              </td>
              <td>
                <button
                  type="button"
                  className="text-button"
                  onClick={(e) => {
                    e.preventDefault();
                    callModal(data.ps_version_id, getDialogTypeFromLoginInfo(data.temp_state, loginUserData.data), index);
                  }}
                >
                  <span className="modal-open">
                    <i className={`fa-solid ${getCheckMarkType(data.temp_state, loginUserData.data)}`} />
                    {getDialogTypeStr(getDialogTypeFromLoginInfo(data.temp_state, loginUserData.data), t)}
                  </span>
                </button>
              </td>
              {/* 削除メニューボタン クリックで削除メニューが表示される */}
              <td className="elloutput">
                <button
                  type="button"
                  className="text-button"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onClickDeleteMark(data.ps_version_id);
                  }}
                >
                  <i className="fa-solid fa-ellipsis-vertical" />
                </button>
                {/* 削除ボタン クリックで削除処理が表示される */}
                {clicked && deleteVersionId === data.ps_version_id && (
                  <div className="open">
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => OnDeleteBtnClick(data.temp_state ?? -1)}
                    >
                      <span>
                        <i className="fa-solid fa-circle-xmark" />
                        {t('削除')}
                      </span>
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
          {/* 確認メッセージ */}
          {confirmDalogFlg && (
            <ConfirmModal
              onClickOk={() => {
                void deleteTemp();
              }}
              onClickCancel={() => {
                setConfirmDalogFlg(false);
              }}
            >
              <div className="poptxt">
                <p>{dialogMessage.current}</p>
              </div>
            </ConfirmModal>
          )}
          {messageDalogFlg && (
            <OkModal
              onClickOk={() => {
                setMessageDalogFlg(false);
              }}
            >
              <div className="poptxt">
                <p>{dialogMessage.current}</p>
              </div>
            </OkModal>
          )}
          {/* 削除エラーの表示 */}
          {deleteErrorModal && <ErrorModal onClickOk={() => setDeleteErrorModal(false)} error={deleteError}>{getDialogMessage(Message.FAILURE_DELETE_PS_TEMPORARY, t)}</ErrorModal>} 
          {/* スピナーモーダル */}
          {displaySpinnerModal && <SpinnerModal />}
        </tbody>
      </table>
      {/* ページング */}
      <PageNavi
        current={currentPage}
        pageCount={Math.ceil(psTempData.data.ps_temps.length / (N_DATA_PER_PAGE * 1.0))}
        onChangePage={changeCurrentPage}
      />
    </div>
  );
});

export default PsTemporaryListTab;
